import { useEffect, useRef, useState } from "react";
import { Share2, Copy, Mail, Check } from "lucide-react";
import { PUBLIC_BASE } from "../lib/publicBase";
import { toast } from "sonner";

/**
 * Share menu — drops into PostCard. Click the Share button to open a popover
 * with one-click share targets to every major social platform plus copy link.
 *
 * Uses each platform's public share URL — no SDKs, no auth, no API keys.
 * Falls back to Web Share API on mobile (native sheet) when available.
 */
export default function ShareMenu({ postId, postTitle = "Cauldron post", postContent = "", testid }) {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  const url = `${PUBLIC_BASE}/feed?post=${postId}`;
  const summary = (postContent || postTitle).slice(0, 200);
  const enc = encodeURIComponent;
  const targets = [
    { id: "facebook", label: "Facebook",  color: "#1877F2", href: `https://www.facebook.com/sharer/sharer.php?u=${enc(url)}&quote=${enc(summary)}` },
    { id: "x",        label: "X / Twitter", color: "#0A0A0A", href: `https://twitter.com/intent/tweet?url=${enc(url)}&text=${enc(summary)}` },
    { id: "linkedin", label: "LinkedIn",  color: "#0A66C2", href: `https://www.linkedin.com/sharing/share-offsite/?url=${enc(url)}` },
    { id: "whatsapp", label: "WhatsApp",  color: "#25D366", href: `https://api.whatsapp.com/send?text=${enc(`${summary} ${url}`)}` },
    { id: "telegram", label: "Telegram",  color: "#229ED9", href: `https://t.me/share/url?url=${enc(url)}&text=${enc(summary)}` },
    { id: "reddit",   label: "Reddit",    color: "#FF4500", href: `https://www.reddit.com/submit?url=${enc(url)}&title=${enc(postTitle)}` },
    { id: "pinterest",label: "Pinterest", color: "#E60023", href: `https://pinterest.com/pin/create/button/?url=${enc(url)}&description=${enc(summary)}` },
    { id: "tumblr",   label: "Tumblr",    color: "#36465D", href: `https://www.tumblr.com/widgets/share/tool?canonicalUrl=${enc(url)}&caption=${enc(summary)}` },
    { id: "email",    label: "Email",     color: "#6E56A8", icon: Mail, href: `mailto:?subject=${enc(postTitle)}&body=${enc(`${summary}\n\n${url}`)}` },
  ];

  const copy = async () => {
    try { await navigator.clipboard.writeText(url); setCopied(true); toast.success("Link copied"); setTimeout(() => setCopied(false), 1800); }
    catch { toast.error("Copy failed"); }
  };

  const onTrigger = async () => {
    // Mobile: prefer the native share sheet when available
    if (navigator.share && /Mobi|Android|iPhone/i.test(navigator.userAgent)) {
      try {
        await navigator.share({ title: postTitle, text: summary, url });
        return;
      } catch { /* user cancelled — fall through to menu */ }
    }
    setOpen((o) => !o);
  };

  return (
    <div ref={ref} className="relative">
      <button onClick={onTrigger} className="flex items-center gap-2 px-3 py-1.5 rounded-sm text-foreground/70 hover:text-secondary hover:bg-secondary/10" data-testid={testid || "share-btn"}>
        <Share2 size={16} strokeWidth={1.5} /> <span className="hidden sm:inline">Share</span>
      </button>
      {open && (
        <div className="absolute z-40 bottom-full right-0 mb-2 w-64 parchment border border-secondary/40 shadow-2xl" data-testid={`${testid || "share-btn"}-menu`}>
          <div className="label-mystic px-4 pt-3 pb-2">Share to</div>
          <ul className="max-h-72 overflow-y-auto scroll-thin">
            {targets.map((t) => (
              <li key={t.id}>
                <a href={t.href} target="_blank" rel="noopener noreferrer"
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/10 transition-colors text-sm"
                  data-testid={`share-${t.id}`}>
                  {t.icon ? (
                    <t.icon size={16} strokeWidth={1.5} style={{ color: t.color }} />
                  ) : (
                    <span className="w-4 h-4 rounded-sm" style={{ background: t.color }} />
                  )}
                  <span className="text-foreground/90">{t.label}</span>
                </a>
              </li>
            ))}
            <li className="border-t border-secondary/15">
              <button onClick={copy}
                className="w-full flex items-center gap-3 px-4 py-2.5 hover:bg-secondary/10 transition-colors text-sm text-left"
                data-testid="share-copy-link">
                {copied ? <Check size={16} strokeWidth={1.75} className="text-secondary" /> : <Copy size={16} strokeWidth={1.5} className="text-foreground/70" />}
                <span className="text-foreground/90">{copied ? "Copied!" : "Copy Link"}</span>
              </button>
            </li>
          </ul>
        </div>
      )}
    </div>
  );
}
